import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGetLoyaltyCardQuery,
  useGetLoyaltyRewardsQuery,
  useRedeemRewardMutation,
  type LoyaltyReward,
} from '../loyaltyApi';
import { useGetRestaurantIdentityQuery } from '@/app/api/hubApi';
import { useAppSelector } from '@/app/hooks';
import { selectCurrentUser } from '@/features/user/selectors';
import { restaurantTz } from '@/lib/hours';
import { firstNameOf, isUsableOnline, redeemErrorMessage, rewardValueLabel } from '../format';
import MemberCard from '../components/MemberCard';
import WalletList from '../components/WalletList';
import LoyaltyHistory from '../components/LoyaltyHistory';
import WalletPassButtons from '../components/WalletPassButtons';
import ConfirmSheet from '../components/ConfirmSheet';
import QrSheet from '../components/QrSheet';
import '../loyalty.css';

// « −10 % » (label) already spelled out in a reward's own name is not repeated.
const squash = (s: string) => s.replace(/[−–]/g, '-').replace(/\s+/g, '').toLowerCase();

const UNAVAILABLE_LABEL = {
  max_uses_reached: 'loyalty.catalog.alreadyUsed',
  out_of_stock: 'loyalty.catalog.soldOut',
} as const;

export default function LoyaltyPage() {
  const { t } = useTranslation();
  const { restaurantUuid = '' } = useParams<{ restaurantUuid: string }>();
  const { data: catalog, isLoading: catalogLoading } = useGetLoyaltyRewardsQuery(restaurantUuid, { skip: !restaurantUuid });
  const {
    data: card,
    isLoading: cardLoading,
    fulfilledTimeStamp: cardFetchedAt,
    refetch: refetchCard,
  } = useGetLoyaltyCardQuery(restaurantUuid, { skip: !restaurantUuid });
  const { data: identity } = useGetRestaurantIdentityQuery(restaurantUuid, { skip: !restaurantUuid });
  const user = useAppSelector(selectCurrentUser);
  const [redeemReward, { isLoading: isRedeeming }] = useRedeemRewardMutation();
  const [confirmReward, setConfirmReward] = useState<LoyaltyReward | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // Fidélité v2: no more throwaway modal — the exchanged coupon lands in the
  // wallet ("Mes avantages"), opened on its QR + code.
  const [unlocked, setUnlocked] = useState<{ code: string; name: string } | null>(null);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [qr, setQr] = useState<{ payload: string; code: string; title: string } | null>(null);

  // Same footprint as the card, so nothing jumps when the data arrives.
  if (catalogLoading || cardLoading) {
    return <div aria-busy="true" className="h-[420px] rounded-btn border-2 border-solid border-hairline bg-paper" />;
  }

  const enabled = catalog?.enabled ?? card?.enabled ?? false;
  const tz = restaurantTz(identity ?? {});
  const walletList = (coupons: NonNullable<typeof card>['wallet']) => (
    <WalletList
      coupons={coupons}
      tz={tz}
      openCode={openCode}
      onToggle={(code) => setOpenCode((current) => (current === code ? null : code))}
      onExpandQr={(coupon) =>
        setQr({
          payload: coupon.qr_payload,
          code: coupon.code,
          title: t('loyalty.wallet.qrLabel', { name: coupon.reward_name }),
        })
      }
      unlocked={unlocked}
    />
  );
  const qrSheet = (
    <QrSheet
      open={qr !== null}
      payload={qr?.payload ?? ''}
      code={qr?.code ?? ''}
      title={qr?.title ?? ''}
      onClose={() => setQr(null)}
    />
  );

  if (!enabled) {
    const programOff = (
      <div className="bg-paper border-2 border-ink p-6 rounded-btn text-center text-ink-mute">
        {t('loyalty.emptyState')}
      </div>
    );
    // SC-1628 — the programme is off, but the coupons already held are still honoured at
    // the counter and by the online order (SC-440): they stay on screen with their QR.
    const held = card?.wallet ?? [];
    if (held.length === 0) return programOff;
    return (
      <div className="loyalty-scope space-y-6 pb-24">
        {programOff}
        {walletList(held)}
        {qrSheet}
      </div>
    );
  }

  const balance = card?.balance ?? catalog?.balance ?? 0;
  // SC-1641 — a reward the hub would refuse anyway (own limit reached, stock gone) is not offered.
  const exchangeable = (r: LoyaltyReward) => balance >= r.points_cost && !r.unavailable;
  // Exchangeable first, then cheapest first: what the customer can act on is on top.
  const rewards = [...(catalog?.rewards ?? [])].sort((a, b) => {
    const affordA = exchangeable(a) ? 1 : 0;
    const affordB = exchangeable(b) ? 1 : 0;
    return affordB - affordA || a.points_cost - b.points_cost;
  });
  const canUnlock = rewards.some(exchangeable);

  const handleExchange = async () => {
    if (!confirmReward) return;
    setErrorCode(null);
    try {
      const result = await redeemReward({ reward_uuid: confirmReward.uuid, restaurant_uuid: restaurantUuid }).unwrap();
      setUnlocked({ code: result.code, name: result.reward_name });
      setOpenCode(result.code);
    } catch (err) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      setErrorCode(detail ?? 'generic');
    } finally {
      setConfirmReward(null);
    }
  };

  return (
    <div className="loyalty-scope space-y-6 pb-24">
      <MemberCard
        restaurantName={identity?.name}
        logoUrl={identity?.logoUrl}
        firstName={firstNameOf(user?.name)}
        balance={balance}
        card={card}
        canUnlock={canUnlock}
        onExpandQr={() => {
          if (card?.qr_payload && card.card_code) {
            setQr({ payload: card.qr_payload, code: card.card_code, title: t('loyalty.card.qrLabel') });
          }
        }}
      />

      {card?.card_code && (
        <WalletPassButtons card={card} fetchedAt={cardFetchedAt} refetch={() => refetchCard().unwrap()} />
      )}

      {card && walletList(card.wallet)}

      <section>
        <h3 className="m-0 mb-3 font-display text-lg text-ink">{t('loyalty.catalogTitle')}</h3>

        {errorCode && (
          <div
            className="mb-3 border-2 border-solid border-sale-red bg-cream text-ink p-3 rounded-btn text-sm"
            data-testid="loyalty-error"
          >
            {redeemErrorMessage(t, errorCode)}
          </div>
        )}

        {rewards.length === 0 ? (
          <div className="bg-paper border-2 border-solid border-ink p-6 rounded-btn text-center text-ink-mute">
            {t('loyalty.emptyState')}
          </div>
        ) : (
          <ul className="m-0 list-none space-y-3 p-0">
            {rewards.map((reward) => {
              const affordable = balance >= reward.points_cost;
              const unavailable = reward.unavailable ? UNAVAILABLE_LABEL[reward.unavailable] : null;
              const valueLabel = rewardValueLabel(t, reward);
              const meta = [
                reward.type === 'custom' ? null : t(`loyalty.types.${reward.type}`),
                valueLabel && !squash(reward.name).includes(squash(valueLabel)) ? valueLabel : null,
              ].filter((part): part is string => Boolean(part));
              return (
                <li
                  key={reward.uuid}
                  className="rounded-btn border-2 border-solid border-ink bg-paper p-4 shadow-stamp-sm"
                >
                  <div className="flex gap-3">
                    {reward.image && (
                      <img
                        src={reward.image}
                        alt=""
                        className="h-16 w-16 flex-none rounded-btn-sm border-2 border-solid border-ink object-cover"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p dir="auto" className="m-0 font-display text-ink">{reward.name}</p>
                      {reward.description && (
                        <p dir="auto" className="m-0 text-sm text-ink-mute">{reward.description}</p>
                      )}
                      <p className="m-0 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-mute">
                        {meta.map((part) => (
                          <bdi key={part} dir="auto">{part}</bdi>
                        ))}
                        <span className="rounded-btn-sm border-2 border-solid border-ink px-2 py-[1px] font-mono text-[11px] font-bold text-ink">
                          {isUsableOnline(reward) ? t('loyalty.wallet.usableOnline') : t('loyalty.wallet.counterOnly')}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <p className="m-0 font-mono text-xs text-ink-mute">
                      {unavailable
                        ? t(unavailable)
                        : !affordable && t('loyalty.catalog.missing', { n: reward.points_cost - balance })}
                    </p>
                    <button
                      type="button"
                      disabled={!affordable || unavailable !== null || isRedeeming}
                      onClick={() => {
                        setErrorCode(null);
                        setConfirmReward(reward);
                      }}
                      className="min-h-[44px] flex-none rounded-btn-sm border-2 border-solid border-ink bg-mint px-4 font-display text-sm text-[var(--on-primary)] disabled:border-[var(--ink-mute)] disabled:bg-transparent disabled:text-[var(--ink-mute)]"
                    >
                      {t('loyalty.redeemButton')} · {t('loyalty.pointsUnit', { n: reward.points_cost })}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {card?.history && <LoyaltyHistory entries={card.history} tz={tz} />}

      <ConfirmSheet
        open={confirmReward !== null}
        name={confirmReward?.name ?? ''}
        costPts={confirmReward?.points_cost ?? 0}
        balance={balance}
        validityDays={confirmReward?.validity_days}
        tz={tz}
        busy={isRedeeming}
        onConfirm={handleExchange}
        onCancel={() => setConfirmReward(null)}
      />
      {qrSheet}
    </div>
  );
}
